import { useState, useEffect, useRef } from 'react';

const playBeep = (freq = 440, type = 'sine', duration = 0.2) => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!window.audioCtx) window.audioCtx = new AudioContext();
        if (window.audioCtx.state === 'suspended') window.audioCtx.resume();
        const oscillator = window.audioCtx.createOscillator();
        const gainNode = window.audioCtx.createGain();
        oscillator.type = type;
        oscillator.frequency.setValueAtTime(freq, window.audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0, window.audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(1, window.audioCtx.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.001, window.audioCtx.currentTime + duration);
        oscillator.connect(gainNode);
        gainNode.connect(window.audioCtx.destination);
        oscillator.start(window.audioCtx.currentTime);
        oscillator.stop(window.audioCtx.currentTime + duration);
    } catch (e) { }
};

export const useTimer = (currentWod, soundEnabled) => {
    const [time, setTime] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [rounds, setRounds] = useState(0);
    const [intervalPhase, setIntervalPhase] = useState('work');
    const [currentIntervalRound, setCurrentIntervalRound] = useState(1);
    const [restTime, setRestTime] = useState(0);
    const [recordedTimes, setRecordedTimes] = useState({});
    const [activeTimerTask, setActiveTimerTask] = useState(null);
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [timerRunning, setTimerRunning] = useState(false);

    // Delta Timer Refs (Onthoudt de werkelijke kloktijd)
    const expectedTimeRef = useRef(Date.now());
    const expectedRestRef = useRef(Date.now());
    const expectedTaskRef = useRef(Date.now());

    // Waarde-refs voor een stabiele tick zonder dubbele React-triggers
    const timeRef = useRef(0);
    const intervalRoundRef = useRef(1);
    const intervalPhaseRef = useRef('work');
    const soundEnabledRef = useRef(soundEnabled);
    const currentWodRef = useRef(currentWod);

    useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
    useEffect(() => { currentWodRef.current = currentWod; }, [currentWod]);

    const setTimeWithRef = (val) => {
        if (typeof val === 'function') {
            setTime(prev => {
                const next = val(prev);
                timeRef.current = next;
                return next;
            });
        } else {
            timeRef.current = val;
            setTime(val);
        }
    };

    const setCurrentIntervalRoundWithRef = (val) => {
        if (typeof val === 'function') {
            setCurrentIntervalRound(prev => {
                const next = val(prev);
                intervalRoundRef.current = next;
                return next;
            });
        } else {
            intervalRoundRef.current = val;
            setCurrentIntervalRound(val);
        }
    };

    const setIntervalPhaseWithRef = (val) => {
        if (typeof val === 'function') {
            setIntervalPhase(prev => {
                const next = val(prev);
                intervalPhaseRef.current = next;
                return next;
            });
        } else {
            intervalPhaseRef.current = val;
            setIntervalPhase(val);
        }
    };

    const initTimerForWod = (wod) => {
        if (window.audioCtx && window.audioCtx.state === 'suspended') window.audioCtx.resume();
        else if (!window.audioCtx) window.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

        setIsRunning(false);
        setRounds(0);
        intervalRoundRef.current = 1;
        setCurrentIntervalRound(1);
        intervalPhaseRef.current = 'work';
        setIntervalPhase('work');
        setRestTime(0);
        setRecordedTimes({});
        setActiveTimerTask(null);
        setTimerSeconds(0);
        setTimerRunning(false);

        const typeStr = String(wod?.type || '').toUpperCase();
        const titleStr = String(wod?.title || '').toUpperCase();
        const isCardio = typeStr.includes('CARDIO') || titleStr.includes('CARDIO');

        let initialTime = 0;
        if (wod?.format === 'AMRAP' || wod?.timerMode === 'countdown') {
            initialTime = wod.timeCap || 12 * 60;
        } else if (isCardio && wod?.timeCap > 0) {
            initialTime = wod.timeCap;
        } else if (wod?.format === 'Tabata') {
            initialTime = 20;
        } else if (wod?.format === 'EMOM') {
            initialTime = 60;
        } else {
            initialTime = 0;
        }

        timeRef.current = initialTime;
        setTime(initialTime);
    };

    const skipToNextPhase = () => {
        const wod = currentWodRef.current || currentWod;
        if (wod?.format === 'Tabata') {
            if (intervalPhaseRef.current === 'work') {
                if (intervalRoundRef.current >= 8) {
                    setIsRunning(false);
                    timeRef.current = 0;
                    setTime(0);
                } else {
                    intervalPhaseRef.current = 'rest';
                    setIntervalPhase('rest');
                    timeRef.current = 10;
                    setTime(10);
                }
            } else {
                intervalPhaseRef.current = 'work';
                setIntervalPhase('work');
                const nextRound = intervalRoundRef.current + 1;
                intervalRoundRef.current = nextRound;
                setCurrentIntervalRound(nextRound);
                timeRef.current = 20;
                setTime(20);
            }
        } else if (wod?.format === 'EMOM') {
            const timeCapMin = wod.timeCap ? wod.timeCap / 60 : null;
            if (timeCapMin && intervalRoundRef.current >= timeCapMin) {
                setIsRunning(false);
                if (soundEnabledRef.current) playBeep(880, 'square', 1);
            } else {
                const nextRound = intervalRoundRef.current + 1;
                intervalRoundRef.current = nextRound;
                setCurrentIntervalRound(nextRound);
                timeRef.current = 60;
                setTime(60);
            }
        }
        expectedTimeRef.current = Date.now() + 1000;
    };

    // Motor 1: Handmatige Focus / Finisher Timer
    useEffect(() => {
        let int;
        if (timerRunning) {
            expectedTaskRef.current = Date.now() + 1000;
            int = setInterval(() => {
                const now = Date.now();
                const delta = now - expectedTaskRef.current;

                if (delta > 500) {
                    const missedSeconds = Math.floor(delta / 1000);
                    setTimerSeconds(s => s + missedSeconds + 1);
                    expectedTaskRef.current = now + 1000 - (delta % 1000);
                } else {
                    setTimerSeconds(s => s + 1);
                    expectedTaskRef.current += 1000;
                }
            }, 1000);
        }
        return () => clearInterval(int);
    }, [timerRunning]);

    // Motor 2: De Rust-Klok
    useEffect(() => {
        let int;
        if (restTime > 0) {
            expectedRestRef.current = Date.now() + 1000;
            int = setInterval(() => {
                const now = Date.now();
                const delta = now - expectedRestRef.current;

                setRestTime(prev => {
                    let deduct = 1;
                    if (delta > 500) {
                        deduct = Math.floor(delta / 1000) + 1;
                        expectedRestRef.current = now + 1000 - (delta % 1000);
                    } else {
                        expectedRestRef.current += 1000;
                    }

                    const nextValue = prev - deduct;

                    if (nextValue <= 0) {
                        if (soundEnabled && prev > 0) playBeep(880, 'square', 0.5);
                        return 0;
                    }
                    if (nextValue <= 3 && prev > 3 && soundEnabled) playBeep(440, 'sine', 0.2);
                    return nextValue;
                });
            }, 1000);
        }
        return () => clearInterval(int);
    }, [restTime, soundEnabled]);

    // Motor 3: De Hoofd-WOD Timer (AMRAP, For Time, Tabata & EMOM)
    useEffect(() => {
        let int;
        if (isRunning && currentWod && !currentWod.isFitness) {
            expectedTimeRef.current = Date.now() + 1000;
            int = setInterval(() => {
                const now = Date.now();
                const delta = now - expectedTimeRef.current;

                let step = 1;
                if (delta > 500) {
                    step = Math.floor(delta / 1000) + 1;
                    expectedTimeRef.current = now + 1000 - (delta % 1000);
                } else {
                    expectedTimeRef.current += 1000;
                }

                const prev = timeRef.current;
                const wod = currentWodRef.current || currentWod;
                const format = wod?.format;
                const typeStr = String(wod?.type || '').toUpperCase();
                const titleStr = String(wod?.title || '').toUpperCase();
                const isCardio = typeStr.includes('CARDIO') || titleStr.includes('CARDIO');
                const isCountdown = format === 'AMRAP' || wod?.timerMode === 'countdown' || (isCardio && wod?.timeCap > 0);

                if (format === 'Tabata') {
                    const nextValue = prev - step;
                    if (nextValue <= 0) {
                        if (intervalPhaseRef.current === 'work') {
                            if (intervalRoundRef.current >= 8) {
                                setIsRunning(false);
                                if (soundEnabledRef.current) playBeep(880, 'square', 0.8);
                                timeRef.current = 0;
                                setTime(0);
                                return;
                            }
                            if (soundEnabledRef.current) playBeep(660, 'square', 0.4);
                            intervalPhaseRef.current = 'rest';
                            setIntervalPhase('rest');
                            const nextRest = Math.max(0, 10 + nextValue);
                            timeRef.current = nextRest;
                            setTime(nextRest);
                            return;
                        } else {
                            if (soundEnabledRef.current) playBeep(880, 'square', 0.6);
                            intervalPhaseRef.current = 'work';
                            setIntervalPhase('work');
                            const nextRound = intervalRoundRef.current + 1;
                            intervalRoundRef.current = nextRound;
                            setCurrentIntervalRound(nextRound);
                            const nextWork = Math.max(0, 20 + nextValue);
                            timeRef.current = nextWork;
                            setTime(nextWork);
                            return;
                        }
                    }
                    if (nextValue <= 3 && prev > 3 && soundEnabledRef.current) playBeep(440, 'sine', 0.2);
                    timeRef.current = Math.max(0, nextValue);
                    setTime(Math.max(0, nextValue));
                    return;
                }

                if (format === 'EMOM') {
                    const nextValue = prev - step;

                    if (nextValue <= 30 && prev > 30 && soundEnabledRef.current) {
                        playBeep(550, 'sine', 0.15);
                    }

                    if (nextValue <= 0) {
                        const timeCapMin = wod.timeCap ? wod.timeCap / 60 : null;
                        if (timeCapMin && intervalRoundRef.current >= timeCapMin) {
                            setIsRunning(false);
                            if (soundEnabledRef.current) playBeep(880, 'square', 1);
                            timeRef.current = 0;
                            setTime(0);
                            return;
                        } else {
                            if (soundEnabledRef.current) playBeep(880, 'square', 0.6);
                            const nextRound = intervalRoundRef.current + 1;
                            intervalRoundRef.current = nextRound;
                            setCurrentIntervalRound(nextRound);
                            const nextTime = Math.max(0, 60 + nextValue);
                            timeRef.current = nextTime;
                            setTime(nextTime);
                            return;
                        }
                    }

                    if (nextValue <= 3 && prev > 3 && soundEnabledRef.current) {
                        playBeep(440, 'sine', 0.2);
                    }

                    timeRef.current = nextValue;
                    setTime(nextValue);
                    return;
                }

                if (isCountdown) {
                    const nextValue = prev - step;
                    if (nextValue <= 0) {
                        setIsRunning(false);
                        if (soundEnabledRef.current && prev > 0) playBeep(880, 'square', 1);
                        timeRef.current = 0;
                        setTime(0);
                        return;
                    }
                    if (nextValue <= 3 && prev > 3 && soundEnabledRef.current) {
                        playBeep(440, 'sine', 0.2);
                    }
                    timeRef.current = nextValue;
                    setTime(nextValue);
                    return;
                }

                // Standaard Stopwatch (For Time)
                const nextValue = prev + step;
                timeRef.current = nextValue;
                setTime(nextValue);
            }, 1000);
        }
        return () => clearInterval(int);
    }, [isRunning, currentWod]);

    useEffect(() => {
        if (isRunning) expectedTimeRef.current = Date.now() + 1000;
        if (timerRunning) expectedTaskRef.current = Date.now() + 1000;
    }, [isRunning, timerRunning]);

    return {
        time, setTime: setTimeWithRef, isRunning, setIsRunning, rounds, setRounds,
        intervalPhase, setIntervalPhase: setIntervalPhaseWithRef, currentIntervalRound, setCurrentIntervalRound: setCurrentIntervalRoundWithRef,
        restTime, setRestTime, recordedTimes, setRecordedTimes,
        activeTimerTask, setActiveTimerTask, timerSeconds, setTimerSeconds,
        timerRunning, setTimerRunning, initTimerForWod, skipToNextPhase
    };
};
